import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT, COMMON_DATETIME_FORMAT } from 'constants/date';
import { ADDRESS_FIELDS_TO_COUNT } from 'constants/loanApplication';
import {
  HistoryPendingChangesActionType,
  HistoryTypeToPendingChangesTypeMap,
  PendingChangeKind,
  PendingChangesActionType,
  pendingChangesSimilarFields,
} from 'constants/pendingChanges';
import { parseCalendarDate } from 'utils/date';
import { jsonParse } from 'utils/json';
import { formatPhoneNumberForDisplay } from 'utils/phone';
import { capitalizeFirstLetterInNameField } from 'utils/string';

import { equalCustomizer } from './form';
import { toPendingChangeKind } from './pendingChangeKind';

export const checkIsPendingChangeHasDifference = ({ newValue, initialValue } = {}) =>
  !_.isEqualWith(newValue, initialValue, equalCustomizer);

// The kind of pending change a value shows, null when nothing is pending.
export const getPendingChangeKind = pendingChange =>
  !_.isNil(pendingChange) && checkIsPendingChangeHasDifference(pendingChange)
    ? toPendingChangeKind(pendingChange.changeKind)
    : null;

export const checkIsDirectPendingChange = pendingChange =>
  getPendingChangeKind(pendingChange) === PendingChangeKind.DIRECT;

const RECALCULATED_SUPPRESSIBLE_ACTION_TYPES = [
  PendingChangesActionType.ADDED,
  PendingChangesActionType.DELETED,
];

// An add or delete the recalculation produced (pendingChangesSource Recalculated) — e.g. a compensation fee the
// comp plan auto-adds on recalc — is pending but not a person's change, so it must neither highlight nor count,
// exactly as a recalculated modified field does not. It carries no per-field detail, so demote the whole action
// to NoChanges. A response without the source (a build that predates it) keeps its action type.
const withoutRecalculatedAction = node =>
  RECALCULATED_SUPPRESSIBLE_ACTION_TYPES.includes(node.pendingChangesActionType) &&
  node.pendingChangesSource === PendingChangeKind.RECALCULATED
    ? { ...node, pendingChangesActionType: PendingChangesActionType.NO_CHANGES }
    : node;

// Loans preview DTOs list every pending field in modifiedFields and say in modifiedFieldDetails whether a person
// changed it or it was recalculated from that change. Only direct changes are highlighted and counted; a response
// without details (a Loans build that predates them) keeps its modifiedFields as they are.
const keepDirectModifiedFields = value => {
  if (Array.isArray(value)) {
    return value.map(keepDirectModifiedFields);
  }

  if (!_.isPlainObject(value)) {
    return value;
  }

  const normalized = _.mapValues(value, keepDirectModifiedFields);

  const withDirectFields = Array.isArray(value.modifiedFieldDetails)
    ? {
        ...normalized,
        modifiedFields: value.modifiedFieldDetails
          .filter(({ source }) => source !== PendingChangeKind.RECALCULATED)
          .map(({ fieldName }) => fieldName),
      }
    : normalized;

  return withoutRecalculatedAction(withDirectFields);
};

export const withDirectModifiedFields = response =>
  response ? { ...response, data: keepDirectModifiedFields(response.data) } : response;

const checkIsRecalculatedFeeChange = fee =>
  fee.pendingChangesSource === PendingChangeKind.RECALCULATED ||
  (!_.isEmpty(fee.modifiedFieldDetails) &&
    fee.modifiedFieldDetails.every(({ source }) => source === PendingChangeKind.RECALCULATED));

// The fee's money fields; other recalculated fee fields (financing, source, ...) are not an amount change.
const AUTOMATIC_FEE_AMOUNT_FIELDS = new Set([
  'amount',
  'borrowerPaidAtClosingAmount',
  'borrowerPaidBeforeClosingAmount',
  'sellerPaidAtClosingAmount',
  'sellerPaidBeforeClosingAmount',
  'paidByOthersAmount',
]);

const getRecalculatedAmountFields = fee =>
  (fee.modifiedFieldDetails ?? [])
    .filter(
      ({ source, fieldName }) =>
        source === PendingChangeKind.RECALCULATED && AUTOMATIC_FEE_AMOUNT_FIELDS.has(fieldName),
    )
    .map(({ fieldName }) => fieldName);

// A fee the recalculation added, removed or re-priced is shown as a pending change (highlighted, counted and tagged)
// with its amount fields; any other recalculated fee field stays hidden, as everywhere else.
const keepAutomaticFeeChange = fee => {
  const directFee = keepDirectModifiedFields(fee);
  const recalculatedAmountFields = getRecalculatedAmountFields(fee);
  const isAutomaticChange =
    checkIsRecalculatedFeeChange(fee) &&
    (RECALCULATED_SUPPRESSIBLE_ACTION_TYPES.includes(fee.pendingChangesActionType) ||
      (fee.pendingChangesActionType === PendingChangesActionType.MODIFIED &&
        !_.isEmpty(recalculatedAmountFields)));

  if (!isAutomaticChange) {
    return { ...directFee, isAutomaticChange: false };
  }

  return {
    ...directFee,
    pendingChangesActionType: fee.pendingChangesActionType,
    modifiedFields: _.uniq([...(directFee.modifiedFields ?? []), ...recalculatedAmountFields]),
    isAutomaticChange: true,
  };
};

// Fees the recalculation added, removed or re-priced stay pending changes, flagged as automatic so the Fees page can
// say so; the rest of the fees view keeps only direct changes.
export const withDirectModifiedFieldsKeepingFeeChanges = response => {
  if (!response?.data) {
    return response;
  }

  const { sections, ...rest } = response.data;

  return {
    ...response,
    data: {
      ...keepDirectModifiedFields(rest),
      sections: _.isNil(sections)
        ? sections
        : _.mapValues(sections, ({ fees, ...section }) => ({
            ...keepDirectModifiedFields(section),
            fees: _.map(fees, keepAutomaticFeeChange),
          })),
    },
  };
};

const EXCLUDED_FROM_CALCULATING_PENDING_CHANGES_FIELDS = [
  'lastModified',
  'modifiedFields',
  'pendingChangesActionType',
];

const OWNED_ENTITY_KEY_PATH_DEPTH = -2;

const composeOwnedEntityKeyPrefix = parentProperty =>
  _.camelCase(parentProperty.split('.').slice(OWNED_ENTITY_KEY_PATH_DEPTH).join(''));

export const composeOwnedEntityPendingChangeKey = ({ parentProperty, propertyName }) =>
  `${composeOwnedEntityKeyPrefix(parentProperty)}${_.upperFirst(_.camelCase(propertyName))}`;

export const composeOwnedEntityPendingChangeKeyMaps = ({
  changeSources,
  excludedFieldNames = [],
}) =>
  Object.entries(changeSources).reduce(
    (acc, [parentProperty, fields]) => {
      Object.entries(fields).forEach(([fieldName, propertyName]) => {
        if (excludedFieldNames.includes(fieldName)) {
          return;
        }

        const pendingChangeKey = composeOwnedEntityPendingChangeKey({
          parentProperty,
          propertyName,
        });

        acc.fieldNameByPendingChangeKey[pendingChangeKey] = fieldName;
        acc.pendingChangeKeyByFieldName[fieldName] = pendingChangeKey;
      });

      return acc;
    },
    {
      fieldNameByPendingChangeKey: Object.create(null),
      pendingChangeKeyByFieldName: Object.create(null),
    },
  );

export const resolvePendingChangePropertyKey = ({
  propertyName,
  parentProperty,
  entityTypeName,
}) => {
  if (parentProperty) {
    return composeOwnedEntityPendingChangeKey({ parentProperty, propertyName });
  }

  if (pendingChangesSimilarFields.includes(propertyName)) {
    return `${_.lowerFirst(entityTypeName)}${capitalizeFirstLetterInNameField(propertyName)}`;
  }

  return propertyName;
};

const COUNTED_AS_SINGLE_PENDING_CHANGE_ACTION_TYPES = [
  PendingChangesActionType.ADDED,
  PendingChangesActionType.DELETED,
];

export const calculatePendingChanges = (pendingChanges, isInitial = false) =>
  Object.entries(pendingChanges).reduce(
    (acc, [fieldName, fieldValue]) => {
      if (
        !_.isObject(fieldValue) ||
        fieldValue.isMirroredChange ||
        EXCLUDED_FROM_CALCULATING_PENDING_CHANGES_FIELDS.includes(fieldName)
      ) {
        return acc;
      }

      if (
        COUNTED_AS_SINGLE_PENDING_CHANGE_ACTION_TYPES.includes(fieldValue.pendingChangesActionType)
      ) {
        const { isSeparateChange } = fieldValue;

        isInitial
          ? (acc[fieldName] = isSeparateChange ? (acc[fieldName] ?? 0) + 1 : 1)
          : (acc = {
              count: isSeparateChange ? (acc.count ?? 0) + 1 : 1,
              shouldBeCountedAsSingleChange: true,
            });

        return acc;
      }

      const isPendingChange = 'newValue' in fieldValue;

      if (isPendingChange) {
        const count = checkIsDirectPendingChange(fieldValue) ? 1 : 0;
        const { isSeparateChange } = fieldValue;

        const shouldBeCountedAsSingleChange =
          (fieldValue.actionType === HistoryPendingChangesActionType.DELETED ||
            fieldValue.actionType === HistoryPendingChangesActionType.ADDED) &&
          !isSeparateChange;

        isInitial
          ? _.set(acc, fieldName, shouldBeCountedAsSingleChange ? 1 : (acc[fieldName] ?? 0) + count)
          : (acc = {
              count: shouldBeCountedAsSingleChange ? 1 : acc.count + count,
              shouldBeCountedAsSingleChange,
            });
      }

      if (!isPendingChange) {
        const { count, shouldBeCountedAsSingleChange } = calculatePendingChanges(fieldValue);

        const parentCount = isInitial ? (_.get(acc, fieldName) ?? 0) : acc.count;

        const separateChangeCount = fieldValue.isSeparateChange ? count : 0;
        const nestedResult =
          parentCount +
          (shouldBeCountedAsSingleChange && parentCount > 0 ? separateChangeCount : count);

        if (isInitial) {
          _.set(acc, fieldName, nestedResult);

          return acc;
        }

        acc.count = nestedResult;
      }

      return acc;
    },
    isInitial ? {} : { count: 0, shouldBeCountedAsSingleChange: false },
  );

const mergePendingChangesCustomizer = (objValue, srcValue, key) => {
  if (key === 'initialValue') {
    return _.isPlainObject(objValue) ? _.merge(srcValue, objValue) : objValue;
  }

  if (key === 'newValue') {
    return srcValue;
  }

  return;
};

const checkIsPendingChangeEntry = value => _.isPlainObject(value) && 'newValue' in value;

// The stored change may be a recalculation; once the user edits that field in the form it is their change.
// A value the form re-derives itself (isDerivedInForm) stays recalculated.
const markFormEditsAsDirect = (mergedPendingChanges, formPendingChanges) =>
  _.forEach(formPendingChanges, (formValue, key) => {
    const mergedValue = mergedPendingChanges?.[key];

    if (checkIsPendingChangeEntry(formValue)) {
      if (
        mergedValue?.changeKind === PendingChangeKind.RECALCULATED &&
        !formValue.isDerivedInForm &&
        checkIsPendingChangeHasDifference(formValue)
      ) {
        mergedValue.changeKind = PendingChangeKind.DIRECT;
      }

      return;
    }

    if (_.isObjectLike(formValue) && _.isObjectLike(mergedValue)) {
      markFormEditsAsDirect(mergedValue, formValue);
    }
  });

export const mergePendingChanges = (
  initialPendingChanges,
  formPendingChanges,
  customizer = mergePendingChangesCustomizer,
) => {
  const mergedPendingChanges = _.mergeWith(
    {},
    initialPendingChanges,
    formPendingChanges,
    customizer,
  );

  markFormEditsAsDirect(mergedPendingChanges, formPendingChanges);

  return mergedPendingChanges;
};

export const formatDatePendingChange = ({
  newValue,
  initialValue,
  format,
  withTime,
  ...restValues
} = {}) => {
  const dateFormat = format ?? (withTime ? COMMON_DATETIME_FORMAT : COMMON_DATE_FORMAT);

  return {
    newValue: newValue && dayjs(newValue).format(dateFormat),
    initialValue: initialValue && dayjs(initialValue).format(dateFormat),
    ...restValues,
  };
};

export const formatCalendarDatePendingChange = ({ newValue, initialValue, ...restValues } = {}) =>
  formatDatePendingChange({
    ...restValues,
    newValue: parseCalendarDate(newValue),
    initialValue: parseCalendarDate(initialValue),
  });

export const getApplicableAddressFieldsPendingChanges = address =>
  _.pick(address, ADDRESS_FIELDS_TO_COUNT);

export const getApplicableResidenceFieldsPendingChanges = residence =>
  _.pick(residence, [
    'startOfResidence',
    'endOfResidence',
    'housingType',
    'monthlyRentAmount',
    'monthlyRelativesPartRentAmount',
    'hasRelativesPartRent',
  ]);

export const checkBooleanPendingChangeVisibility = ({ newValue, initialValue } = {}) =>
  !(!newValue && !initialValue);

export const fillPendingChangesWithActionType = ({
  pendingChanges,
  newRecords = [],
  deletedRecords = [],
  newAddedInitialRecords = [],
}) => {
  [...newRecords, ...newAddedInitialRecords].forEach(recordId =>
    _.set(pendingChanges, `[${recordId}].pendingChangesActionType`, PendingChangesActionType.ADDED),
  );

  deletedRecords.forEach(recordId => {
    if (newAddedInitialRecords.includes(recordId)) {
      return _.unset(pendingChanges, recordId);
    }

    _.set(
      pendingChanges,
      `[${recordId}].pendingChangesActionType`,
      PendingChangesActionType.DELETED,
    );
  });

  return pendingChanges;
};

export const getNewAndDeletedEntities = entities => {
  return entities.reduce(
    (acc, cur) => {
      if (cur.pendingChangesActionType === PendingChangesActionType.DELETED) {
        acc.deletedEntities.push(cur.id);
      }

      if (cur.pendingChangesActionType === PendingChangesActionType.ADDED) {
        acc.newEntities.push(cur.id);
      }

      return acc;
    },
    { deletedEntities: [], newEntities: [] },
  );
};

export const formatPhoneNumberPendingChange = ({ newValue, initialValue } = {}) => ({
  newValue: newValue && formatPhoneNumberForDisplay(newValue),
  initialValue: initialValue && formatPhoneNumberForDisplay(initialValue),
});

export const replaceNestedFieldsActionTypeToModified = dataToReplace =>
  Object.entries(dataToReplace).reduce((acc, [fieldName, value]) => {
    acc[fieldName] = {
      ...value,
      actionType: HistoryPendingChangesActionType.MODIFIED,
    };

    return acc;
  }, {});

export const extractPendingChangesFromJson = value => {
  const { newValue, initialValue, actionType } = value;

  const parseJsonArray = jsonData =>
    jsonParse(jsonData).map(item => _.mapKeys(item, (__, key) => _.lowerFirst(key)));

  const parsedNewValue = newValue ? parseJsonArray(newValue) : [];
  const parsedInitialValue = initialValue ? parseJsonArray(initialValue) : [];

  return parsedNewValue.map((item, index) =>
    _.mapValues(item, (value, key) => ({
      newValue: value,
      initialValue: parsedInitialValue[index]?.[key] ?? null,
      actionType,
    })),
  );
};

export const filterOptionalVisiblePendingChanges = ({
  pendingChanges,
  optionalVisibleFieldsTriggers,
  ...restValidationProps
}) => {
  return optionalVisibleFieldsTriggers.reduce((acc, { fieldName, validation, dependents }) => {
    const isValid = validation({
      pendingChange: _.get(acc, fieldName),
      pendingChanges: acc,
      ...restValidationProps,
    });

    if (!isValid) {
      return acc;
    }

    return _.omit(acc, dependents);
  }, pendingChanges);
};

export const normalizePendingChanges = ({
  pendingChanges,
  fieldsPendingChangeNormalizers,
  ...resetNormalizeProps
}) => {
  const normalizedPendingChanges = pendingChanges;

  fieldsPendingChangeNormalizers.forEach(({ fieldName, normalize }) => {
    const fieldPendingChange = _.get(normalizedPendingChanges, fieldName);

    if (!fieldPendingChange) {
      return;
    }

    const normalizedPendingChange = normalize({
      pendingChanges: normalizedPendingChanges,
      pendingChange: fieldPendingChange,
      ...resetNormalizeProps,
    });

    _.set(normalizedPendingChanges, fieldName, normalizedPendingChange);
  });

  return normalizedPendingChanges;
};

export const fillRowsWithPendingChangesFields = ({
  data,
  pendingChanges,
  dataPrimaryKey = 'id',
}) => {
  const documentExpirationDatesMapById = _.keyBy(data, dataPrimaryKey);

  const preparedDocumentExpirationDatesMapById = Object.entries(pendingChanges).reduce(
    (acc, [entityId, entityChanges]) => {
      const { pendingChangesActionType, modifiedFields, initialValues } = Object.entries(
        entityChanges,
      ).reduce(
        (acc, [entityChangeKey, entityChangeValue]) => {
          acc.pendingChangesActionType =
            acc.pendingChangesActionType ??
            HistoryTypeToPendingChangesTypeMap[entityChangeValue.actionType];
          acc.initialValues[entityChangeKey] = entityChangeValue.initialValue;

          if (checkIsPendingChangeHasDifference(entityChangeValue)) {
            acc.modifiedFields.push(_.upperFirst(entityChangeKey));
          }

          return acc;
        },
        { pendingChangesActionType: null, modifiedFields: [], initialValues: {} },
      );

      acc[entityId] =
        pendingChangesActionType === PendingChangesActionType.DELETED
          ? {
              ...initialValues,
              pendingChangesActionType,
            }
          : {
              ...acc[entityId],
              modifiedFields,
              pendingChangesActionType,
            };

      return acc;
    },
    { ...documentExpirationDatesMapById },
  );

  return Object.values(preparedDocumentExpirationDatesMapById);
};
